import { ReactElement, useCallback } from 'react'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import { Button } from '@mui/material'
import type { ShearConnectionDTO } from '@api/fastAPI.schemas'
import { useSnackbar } from '@ui/snackbar'
import {
  parseShearConnectionFromClipboardText,
  preparePastedShearConnection,
} from '../utils/clipboardShearConnection'

interface Props {
  onPaste: (connection: ShearConnectionDTO) => void
}

/** Reads a Schubverbinder copied from a list row and hands it over as a new, unsaved copy. */
const PasteShearConnectionFromClipboardButton = ({ onPaste }: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()

  const handlePaste = useCallback(async () => {
    try {
      const connectionData = await navigator.clipboard.readText()
      const connection = parseShearConnectionFromClipboardText(connectionData)

      if (!connectionData.trim()) {
        enqueueSnackbar('Kein Schubverbinder in der Zwischenablage', { variant: 'warning' })
        return
      }

      if (!connection) {
        enqueueSnackbar('Fehler beim Einfügen des Schubverbinders', { variant: 'error' })
        return
      }

      onPaste(preparePastedShearConnection(connection))
      enqueueSnackbar('Schubverbinder eingefügt', { variant: 'success' })
    } catch {
      enqueueSnackbar('Fehler beim Einfügen des Schubverbinders', { variant: 'error' })
    }
  }, [enqueueSnackbar, onPaste])

  return (
    <Button
      startIcon={<ContentPasteIcon />}
      onClick={handlePaste}
      variant="outlined"
      size="small"
      data-cy="paste-shear-connection-from-clipboard"
    >
      Schubverbinder aus Zwischenablage einfügen
    </Button>
  )
}

export default PasteShearConnectionFromClipboardButton
